/* eslint-disable no-nested-ternary */
import React, { useCallback, useState } from 'react';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import isObject from 'lodash/isObject';
import HipchatChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';

import { debugTableStyles, nullCellStyles, sortCellStyles, sortCellIconStyles } from './styles';

type DebugInfoTableProps = {
  data: any[];
  pickFields?: string[];
};

enum SortMethods {
  ASC = 'asc',
  DESC = 'desc',
  NONE = 'none',
}

const DebugInfoTable: React.FC<DebugInfoTableProps> = ({ data, pickFields = [] }) => {
  let rows = data;

  const [sortMethod, setSortMethod] = useState<SortMethods>(SortMethods.NONE);
  const [sortField, setSortField] = useState<string | null>(null);

  const handleClickSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        switch (sortMethod) {
          case SortMethods.ASC:
            setSortMethod(SortMethods.DESC);
            break;
          case SortMethods.DESC:
            setSortMethod(SortMethods.NONE);
            setSortField(null);
            break;
        }
      } else {
        setSortMethod(SortMethods.ASC);
        setSortField(field);
      }
    },
    [sortField, sortMethod]
  );

  if (!rows) return null;

  const restFields: string[] = Object.keys(omit(rows[0] || {}, pickFields));
  const mergedFields: string[] = [...pickFields, ...restFields];

  if (sortField && sortMethod !== SortMethods.NONE) {
    rows = orderBy(rows, [sortField], [sortMethod]);
  }

  return (
    <table css={debugTableStyles}>
      <tr>
        {mergedFields.map((field: string) => (
          <th
            key={field}
            css={sortField === field ? sortCellStyles : ''}
            onClick={() => {
              handleClickSort(field);
            }}
          >
            {field}
            {sortField === field && (
              <span css={sortCellIconStyles}>
                {sortMethod === SortMethods.ASC ? (
                  <HipchatChevronUpIcon label="" size="small" />
                ) : (
                  <HipchatChevronDownIcon label="" size="small" />
                )}
              </span>
            )}
          </th>
        ))}
      </tr>
      {rows.length > 0 &&
        rows.map((datum: any, index: number) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index}>
            {mergedFields.map((field: string) => (
              <td key={field}>
                {datum[field] === null ? (
                  <span css={nullCellStyles}>NULL</span>
                ) : isObject(datum[field]) ? (
                  JSON.stringify(datum[field])
                ) : (
                  datum[field].toString()
                )}
              </td>
            ))}
          </tr>
        ))}
    </table>
  );
};

export default DebugInfoTable;
